// A vertical scale fitted to the data it draws (2026-10-08): chosen
// quantiles of the values are pinned to chosen heights, the ends fixed, and
// the scale is linear between the knots. The dots then spread over the plot
// whatever the values' range: the Accuracy chart pins the accuracies'
// quantiles between 100 and 0; the swings charts pin each side's quantiles
// between 0 and ±100. Monotone by construction: a knot that does not fall
// strictly beyond the one before it is dropped.

/** The value at a quantile of sorted ascending values, interpolated. */
export function quantile(sorted: readonly number[], q: number): number {
  if (!sorted.length) return NaN;
  const pos = (sorted.length - 1) * q;
  const lo = Math.floor(pos);
  const hi = Math.ceil(pos);
  return sorted[lo] + (sorted[hi] - sorted[lo]) * (pos - lo);
}

/** A knot: a value and the position (0–1) it is pinned to. */
export type Knot = [value: number, position: number];

/**
 * The knots of a fitted scale over `values`, from `start` (position 0) to
 * `end` (position 1): each quantile in `quantiles` at its position in
 * `positions`, kept only when strictly between the neighbouring values (at
 * least `gap` away), so the knots are strictly monotone in value.
 */
export function fitKnots(values: readonly number[], start: number, end: number, quantiles: readonly number[], positions: readonly number[], gap = 0.5): Knot[] {
  const sorted = [...values].sort((a, b) => a - b);
  const up = end > start;
  const knots: Knot[] = [[start, 0]];
  quantiles.forEach((q, i) => {
    const v = quantile(sorted, q);
    const last = knots[knots.length - 1][0];
    const inside = up ? v > last + gap && v < end - gap : v < last - gap && v > end + gap;
    if (inside) knots.push([v, positions[i]]);
  });
  knots.push([end, 1]);
  return knots;
}

/** The position (0–1) of a value on a piecewise-linear scale through the knots; clamped to the ends. */
export function positionOf(knots: readonly Knot[], value: number): number {
  const [v0] = knots[0];
  const [vn] = knots[knots.length - 1];
  const up = vn > v0;
  const v = up ? Math.max(v0, Math.min(vn, value)) : Math.min(v0, Math.max(vn, value));
  for (let i = 1; i < knots.length; i++) {
    const [a, pa] = knots[i - 1];
    const [b, pb] = knots[i];
    if (up ? v <= b : v >= b) return pa + ((v - a) / (b - a)) * (pb - pa);
  }
  return 1;
}

/** The value at a position (0–1): the inverse of `positionOf`. */
export function valueAt(knots: readonly Knot[], position: number): number {
  const p = Math.max(0, Math.min(1, position));
  for (let i = 1; i < knots.length; i++) {
    const [a, pa] = knots[i - 1];
    const [b, pb] = knots[i];
    if (p <= pb) return a + ((p - pa) / (pb - pa)) * (b - a);
  }
  return knots[knots.length - 1][0];
}

/** The knots' values rounded as ticks, in knot order, without near-duplicates (under `apart`). */
export function knotTicks(knots: readonly Knot[], apart = 4): number[] {
  return knots.map(([v]) => Math.round(v)).filter((t, i, all) => i === 0 || Math.abs(all[i - 1] - t) >= apart);
}
